import React, {FunctionComponent, useCallback, useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {RouteComponentProps, withRouter} from 'react-router-dom';
import {appBar} from '../../styles';
import {
    Button,
    Checkbox,
    CircularProgress,
    Container,
    CssBaseline,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Theme,
    Typography
} from '@material-ui/core';
import {createStyles, withStyles} from '@material-ui/core/styles';
import {AppState, Token} from '../../helpers/types';
import {StudentsForSession} from '../../helpers/responseTypes';
import AppBar from '../AppBar';
import DrawerMenu from '../DrawerMenu';
import {SHOW_ALL_GROUPS_ROUTE} from './routes';
import {Connector} from '../../helpers/Connector';
import {SnackBarMessage} from '../../components';
import i18n from '../../i18n/i18n';
import {SnackBarType} from '../../components/SnackBarMessage';
import {KeyboardDateTimePicker} from '@material-ui/pickers';
import {CheckBoxOutlineBlank} from '@material-ui/icons';
import {RequestState, Session, SessionStudent} from '../../redux/session/types';
import {ThunkDispatch} from 'redux-thunk';
import {AnyAction} from 'redux';
import {
    actionFailed,
    actionRequested,
    actionSucceeded,
    assignStudentToSessionAction,
    bulkAssignStudentToSession,
    clearStudentsAction,
    editStudentSessionAction,
    resetSession,
    setSessionDate,
    submitSession
} from '../../redux/session/actions';
import {Moment} from 'moment';

const styles = (theme: Theme) => createStyles({
    root: {
        display: 'flex',
    },
    content: {
        flex: 1,
        width: '100%',
        marginTop: appBar.height,
        paddingTop: theme.spacing(2),
        alignItems: 'center',
        justifyContent: 'center',
    },
    titleView: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    attendanceForm: {
        display: 'flex',
        flexDirection: 'row',
    },
    submitView: {
        display: 'flex',
        flexDirection: 'column',
        flex: 0.2,
    },
    studentList: {
        flex: 0.8
    }
});

type StateProps = {
    token: Token;
    login: string;
    isAdmin: boolean;
    isCoach: boolean;
    isSectionManager: boolean;
    groupId: number | null;
    subGroupId: number | null;
    date: Date;
    students: SessionStudent[];
    request: RequestState;
    isTransmitted: boolean;
    name: string;
};

type DispatchProps = {
    actionRequested: () => void;
    actionSucceeded: () => void;
    actionFailed: (message: string) => void;
    assignStudentToSession: (
        studentId: number,
        isLate: boolean,
        isAbsent: boolean,
        firstName: string,
        lastName: string,
    ) => void;
    editStudentSession: (
        studentId: number,
        isLate: boolean,
        isAbsent: boolean,
    ) => void;
    resetSession: () => void;
    clearStudents: () => void;
    setSessionDate: (date: Date) => void;
    bulkAssignStudentToSession: (students: SessionStudent[]) => void;
    submitSession: (session: Session) => void;
};

type Props = {
    classes: { [key: string]: string };
} & StateProps & DispatchProps & RouteComponentProps;

const NewSession: FunctionComponent<Props> = ({
    classes,
    token,
    login,
    history,
    groupId,
    subGroupId,
    date,
    students,
    request,
    isTransmitted,
    name,
    editStudentSession,
    resetSession,
    clearStudents,
    bulkAssignStudentToSession,
    setSessionDate,
    submitSession,
}: Props) => {
    const [sessionDateError, setSessionDateError] = useState(!date || (date && date.toString() === 'Invalid Date'));
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    useEffect(() => {
        if (!token.access) {
            history.push('/login');
        }
    }, [history, token.access]);

    useEffect(() => {
        return (): void => {
            clearStudents();
        };
    }, [clearStudents]);

    const fetchStudents = useCallback(() => {
        actionRequested();
        Connector.getInstance().getStudentsForSession(groupId || 0, subGroupId || 0)
            .then(studentsResponse => {
                if ((studentsResponse as StudentsForSession).keys()) {
                    bulkAssignStudentToSession((studentsResponse as StudentsForSession).sort(
                        (a,
                            b) =>
                            a.lastName < b.lastName
                                ? -1
                                : a.lastName > b.lastName
                                    ? 1 : 0).map(student => {
                        return {
                            id: student.id,
                            isLate: false,
                            isAbsent: true,
                            firstName: student.firstName,
                            lastName: student.lastName
                        };
                    }));
                } else {
                    actionFailed((studentsResponse as string));
                }
            })
            .catch((e: Error): void => {
                e.message === 'LOGOUT' && history.push('/login');
            });
    }, [bulkAssignStudentToSession, groupId, history, subGroupId]);

    useEffect(() => {
        if (students.length === 0 || !request.succeeded)
            fetchStudents();
    }, [fetchStudents, students.length, request.succeeded]);

    useEffect(() => {
        setSessionDateError(!date || (date && date.toString() === 'Invalid Date'));
    }, [date]);

    const handleSessionDateChange = (newDate: Moment | null): void => {
        if (newDate) {
            setSessionDate(newDate.toDate());
        } else {
            setSessionDate(new Date('Invalid'));
        }
    };

    const handleSessionDateBlur = (): void => {
        setSessionDateError(!date || (date && date.toString() === 'Invalid Date'));
    };

    const handleSubmitClick = (): void => {
        submitSession({
            groupId,
            name,
            subGroupId,
            date,
            students,
            request,
            isTransmitted,
        });
    };

    const handleCancelClick = (): void => {
        resetSession();
        history.push('/group');
    };

    const isAttendingChecked = (id: number): boolean =>
        !students.filter(student => student.id === id)[0].isAbsent;

    const handleIsAttendingChecked = (id: number): void => {
        const student = students.filter(student => student.id === id)[0];
        editStudentSession(student.id, student.isLate, !student.isAbsent);
    };

    const isLateChecked = (id: number): boolean =>
        students.filter(student => student.id === id)[0].isLate;

    const handleIsLateChecked = (id: number): void => {
        const student = students.filter(student => student.id === id)[0];
        editStudentSession(student.id, !student.isLate, student.isLate ? student.isAbsent : false);
    };

    useEffect(() => {
        if (request.succeeded && isTransmitted) {
            resetSession();
            history.push('/group/');
        }
    }, [history, isTransmitted, request.succeeded, resetSession]);

    return (
        <div className={classes.root}>
            <CssBaseline/>
            <AppBar currentRoute={SHOW_ALL_GROUPS_ROUTE} login={login} onBurgerClick={() => {
                setIsMenuOpen(!isMenuOpen);
            }}/>
            <DrawerMenu isOpen={isMenuOpen}/>
            {request.requested ?
                <Container component="main" className={classes.content}>
                    <CircularProgress/>
                </Container> :
                <Container component="main" className={classes.content}>
                    <div className={classes.titleView}>
                        <Typography variant="h3" component="h6">
                            {i18n.t('NEW_SESSION', {name})}
                        </Typography>
                        <KeyboardDateTimePicker
                            required
                            id="birthDate"
                            label={i18n.t('SESSION_DATE')}
                            value={date}
                            onChange={handleSessionDateChange}
                            error={sessionDateError}
                            format="DD/MM/YYYY HH:mm"
                            onBlur={handleSessionDateBlur}
                            invalidDateMessage={i18n.t('INVALID_DATE_MESSAGE')}
                        />
                    </div>
                    <div className={classes.attendanceForm}>
                        <div className={classes.submitView}>
                            <Button variant="contained" color="primary"
                                disabled={sessionDateError || isTransmitted}
                                onClick={handleSubmitClick}>
                                {i18n.t('SUBMIT_BUTTON')}
                            </Button>
                            <Button variant="contained" color="default"
                                onClick={handleCancelClick}>
                                {i18n.t('CANCEL_BUTTON')}
                            </Button>
                        </div>
                        <Paper className={classes.studentList}>
                            {students.length ? <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{i18n.t('ATTENDING')}</TableCell>
                                        <TableCell>{i18n.t('IS_LATE')}</TableCell>
                                        <TableCell>{i18n.t('LAST_NAME_FIELD')}</TableCell>
                                        <TableCell>{i18n.t('FIRST_NAME_FIELD')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {students.map(student =>
                                        <TableRow key={student.id}>
                                            <TableCell>
                                                <Checkbox checked={isAttendingChecked(student.id)} value="checked"
                                                    icon={<CheckBoxOutlineBlank color="primary"/>}
                                                    color="primary"
                                                    onChange={() => {
                                                        handleIsAttendingChecked(student.id);
                                                    }}/>
                                            </TableCell>
                                            <TableCell>
                                                <Checkbox checked={isLateChecked(student.id)} value="checked"
                                                    icon={<CheckBoxOutlineBlank color="primary"/>}
                                                    color="primary"
                                                    onChange={() => {
                                                        handleIsLateChecked(student.id);
                                                    }}/>
                                            </TableCell>
                                            <TableCell>
                                                {student.lastName}
                                            </TableCell>
                                            <TableCell>
                                                {student.firstName}
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                                :
                                <Typography paragraph>
                                    {i18n.t('NO_STUDENT_TEXT')}
                                </Typography>}
                        </Paper>
                    </div>
                </Container>}
            <SnackBarMessage
                content={isTransmitted && request.succeeded ? i18n.t('CREATE_SESSION_SUCCESS') : request.failMessage}
                type={isTransmitted && request.succeeded ? SnackBarType.SUCCESS : SnackBarType.ERROR}
                isOpen={(isTransmitted && request.succeeded) || request.failed}/>
        </div>
    );
};

const mapStateToProps = (state: AppState): StateProps => {
    const {token, login, isAdmin, isCoach, isSectionManager} = state.login;
    return {token, login, isAdmin, isCoach, isSectionManager, ...state.session};
};

const mapDispatchToProps = (dispatch: ThunkDispatch<AppState, unknown, AnyAction>): DispatchProps => ({
    actionRequested: (): void => {
        dispatch(actionRequested());
    },
    actionSucceeded: (): void => {
        dispatch(actionSucceeded());
    },
    actionFailed: (message: string): void => {
        dispatch(actionFailed(message));
    },
    assignStudentToSession: (
        studentId: number,
        isLate: boolean,
        isAbsent: boolean,
        firstName: string,
        lastName: string,
    ): void => {
        dispatch(assignStudentToSessionAction(studentId, isLate, isAbsent, firstName, lastName));
    },
    resetSession: (): void => {
        dispatch(resetSession());
    },
    clearStudents: (): void => {
        dispatch(clearStudentsAction());
    },
    editStudentSession: (
        studentId: number,
        isLate: boolean,
        isAbsent: boolean,
    ): void => {
        dispatch(editStudentSessionAction(studentId, isLate, isAbsent));
    },
    bulkAssignStudentToSession: (students: SessionStudent[]): void => {
        dispatch(bulkAssignStudentToSession(students));
    },
    setSessionDate: (date: Date): void => {
        dispatch(setSessionDate(date));
    },
    submitSession: (session: Session): void => {
        dispatch(submitSession(session));
    },
});

export default withStyles(styles)(withRouter(connect(mapStateToProps, mapDispatchToProps)(NewSession)));
